import { useState, useEffect } from 'react';
import { css } from 'aphrodite/no-important';
import { Route, Switch, withRouter } from 'react-router-dom';
import { LocalStorage } from 'ttl-localstorage';

import AdUnit from 'components/ad-unit/index';
import Categories from 'components/categories/index';
import Closed from 'components/closed/index';
import Confirmation from 'components/thanks/index';
import CookieConsent from 'components/cookie-consent';
import Countdown from 'components/countdown/index';
import Dropdown from 'components/dropdown/index';
import FbError from 'components/fb-error/index';
import ErrorMessage from 'components/error-message/index';
import Footer from 'components/footer/index';
import Header from 'components/header/index';
import Language from 'components/language/index';
import Loading from 'components/loading/index';
import Login from 'components/login/index';
import Navigation from 'components/navigation/index';
import Modal from 'components/modal/index';
import Grid from 'components/grid/index';
import Share from 'components/share/index';
import User from 'components/user/index';
import Vote from 'components/vote/index';


import * as models from 'models/index';

import { Connect } from 'store/index';

import api from 'util/api';
import * as constants from 'util/constants';
import * as fbHelpers from 'util/fb-helpers';
import * as googleHelpers from 'util/google-helpers';
import { loginWithJwt } from 'util/twitter';
import { checkIfTrue, insertFonts, isInsideInstagramWebview, getQueryParamByName, storeUserData} from 'util/helpers';
import { storageFactory } from 'util/storage_helpers';
import useGeo from '@telescope/react-hooks/useGeo';
import useCMS from '@telescope/react-hooks/useCMS';
import { style } from './style';

const localStore = storageFactory(localStorage);
const MIN_POLLING_RATE = 5000;

function App(props: models.store.IAppProps) {

  // Main Widget
  const { sid = '', wid = api.defaults.wid } = api.storage.qsps;
  const [ pollingFrequency, setPollingFrequency ] = useState(MIN_POLLING_RATE);
  const { lang } = fbHelpers.parseFacebookStateParams();
  const [ language, setLanguage ] = useState( lang || api.defaults.lang);
  const [ widget ] = useCMS<models.api.ICmsData>(wid, { sid, pollingFrequency });
  const [ shouldBootstrapManualOAuth, setShouldBootStrapManualOAuth ] = useState( true );

  // Supporting Widgets
  const { copy_sid: copySid = '', copy_wid: copyWid = widget?.text.snapshot_settings[`copy_wid_${language}`],
            contestants_sid: contestantsSid = '', contestants_wid: contestantsWid = widget?.text.snapshot_settings.contestants_wid,
            styles_sid: stylesSid = '', styles_wid: stylesWid = widget?.text.snapshot_settings.styles_wid
    } = api.storage.qsps;

  const [ copyWidget ] = useCMS<models.api.ICmsData>(copyWid, { sid: copySid, alwaysReturnCachedResponse: true, pollingFrequency: widget?.text.snapshot_settings.polling_frequency || MIN_POLLING_RATE });
  const [ contestantsWidget ] = useCMS<models.api.ICmsData>(contestantsWid, { sid: contestantsSid, pollingFrequency: widget?.text.snapshot_settings.polling_frequency || MIN_POLLING_RATE });
  const [ stylesWidget ] = useCMS<models.api.ICmsData>(stylesWid, { sid: stylesSid, pollingFrequency: widget?.text.snapshot_settings.polling_frequency || MIN_POLLING_RATE });

  const geo = useGeo(widget?.settings.countries);

  const changeLanguage = ( type: string ) => {
    setLanguage(type);
  };

  const readLocalStore = async () => {
    const storedUser = localStore.getItem(constants.AUTH_LOCALSTORAGE_LABEL);

    if (!storedUser) {
      return;
    }

    const payload = JSON.parse(storedUser);
    const { method } = payload;

    switch (method) {
      case constants.AUTH_METHODS.EMAIL:
        await props.authFn.login(payload, method);
        break;

      case constants.AUTH_METHODS.TWITTER:
        const { twitter_auth_key, twitter_auth_url } = props.cmsData.settings;

        const twitterPayload = await loginWithJwt(payload.id, twitter_auth_key, twitter_auth_url) as models.auth.ITwitterLoginPayload;
        await props.authFn.login({ ...twitterPayload, ...payload }, method);
        break;

      case constants.AUTH_METHODS.FACEBOOK:
        fbHelpers.checkLoginState()
        .then((res) => props.authFn.login({ ...res, ...payload }, method))
        .catch(() => {
          //Do nothing
        });
        break;
      default:
        // if no valid method is found, remove auth from localstore
        localStore.removeItem(constants.AUTH_LOCALSTORAGE_LABEL);
    }
  };

  /**
   * Handler to be executed when a user accepts cookies. Should be used to initialize any
   * analytics tracking or set cookies that are not essential to the functionality of the app.
   */
  const handleCookieConsentAccepted = () => {
    googleHelpers.initializeGoogleAnalytics(widget.settings.google_analytics);
  }

  /**
   * Handler to be executed when a user declines cookies. Should be used to destroy any initialized
   * analytics trackers.
   */
  const handleCookieConsentDeclined = () => {
    // Currently a noop. Reserved for future if needed.
  }

  // Bootstrap app after data is fetched
  useEffect(() => {
    if (!widget || !copyWidget || !contestantsWidget || !stylesWidget) return;

    async function bootstrap() {
      props.cmsFn.storeCmsData(widget, copyWidget, contestantsWidget, stylesWidget.text, language);

      // Check if cookie consent is required, and if so, has consent been granted
      const isCookieConsentRequired = checkIfTrue(widget.text.cookie_consent.enabled);
      let isCookieConsentGiven = !isCookieConsentRequired || LocalStorage.get(widget.text.cookie_consent.storage_key);

      // Initialize FB SDK if FB is needed for login or share
      if (checkIfTrue(widget.text.login.settings.display_facebook) ||
          checkIfTrue(widget.text.share.settings.share_contestant)) {
        fbHelpers.initializeFbSdk(widget.social.fb.id);
      }

      const pym = await props.globalFn.setPym();

      let loggedInViaManualFlow = false;

      if( isInsideInstagramWebview() && shouldBootstrapManualOAuth ) {
        const { accessToken, accessTokenExpiration } = fbHelpers.getFacebookTokens();
        const { granted } = fbHelpers.getFacebookScopes();
        const hasEmailPermission = granted.indexOf('email') >= 0;
        
        if( accessToken && accessTokenExpiration && hasEmailPermission ) {
          const payload = await fbHelpers.getManualLoginUserData( accessToken, accessTokenExpiration );

          if( payload && payload.user_id ) {
            const loginResponse = await props.authFn.submitLogin( payload );

            if ( loginResponse.response_code === constants.RESPONSE_CODES.VALID ) {
              storeUserData(constants.AUTH_METHODS.FACEBOOK, payload.fb_email, payload);
              await props.authFn.login(payload, constants.AUTH_METHODS.FACEBOOK);
              loggedInViaManualFlow = true;
              fbHelpers.restorePreviousRoute( props.gridFn );
              
              props.globalFn.scrollToTop();
            }
          }

          pym.scrollParentTo('');

        } else if( accessToken && accessTokenExpiration && !hasEmailPermission ) {
          fbHelpers.restorePreviousRoute( props.gridFn, false );
          props.loginFn.setFbError(fbHelpers.FB_ERROR_TYPE.PERMISSIONS);
          props.modalFn.openModal(constants.MODAL_TYPES.login);

          pym.scrollParentTo('');
        } else {
          const { error, errorCode } = fbHelpers.getFacebookErrorParams();

          if( error && errorCode ) {
            fbHelpers.restorePreviousRoute( props.gridFn );
          }
        }
      }

      // This useEffect hook is rerun every time a widget changes/polls,
      // but we don't need to go through the manual oauth stuff again.
      setShouldBootStrapManualOAuth( false );

      if( !loggedInViaManualFlow ) {
        await readLocalStore();
      }

      // Analytics
      if (isCookieConsentGiven) {
        googleHelpers.initializeGoogleAnalytics(widget.settings.google_analytics);
      }
      googleHelpers.addLinkClickListener();

    }

    bootstrap();

    return () => {
      googleHelpers.removeLinkClickListener();
    }

  }, [widget, copyWidget, contestantsWidget, stylesWidget]);

  // Handle font import after styles data is stored
  useEffect(() => {
    if (props.stylesData.global) {
      insertFonts(props.stylesData.global.font.fontImport);
    }
  }, [props.stylesData])

  if (!props.isAppReady) {
    return <Loading />;
  }

  // Render app
  const { text, settings } = props.cmsData;
  const copy = props.copyData.text;
  const { stylesData } = props;
  const styles = style({
    pageStyles: props.stylesData.app,
    globalStyles: props.stylesData.global,
    modalOpen: props.modalProps.type !== ''
  });
  const gridEnabled = (checkIfTrue(settings.window_status) || (!checkIfTrue(settings.window_status) && checkIfTrue(text.closed.settings.display_grid)));

  const ComposedLogin = (
    <Login SwapLoading={Loading}
      SwapError={FbError} />
  );

  const modalMap: {[key: string]: any} = {
    confirmation: (
      <Confirmation>
        <Share language={language}
               displayFacebook={checkIfTrue(props.cmsData.text.thank_you.settings.display_share.display_facebook)}
               displayTwitter={checkIfTrue(props.cmsData.text.thank_you.settings.display_share.display_twitter)} />
      </Confirmation>),
    errorGeneric: <ErrorMessage data={copy.errors.generic} styles={stylesData.errors.generic}/>,
    errorOverlimit: <ErrorMessage data={copy.errors.overlimit} styles={stylesData.errors.overlimit}/>,
    errorWindow: <ErrorMessage data={copy.errors.window} styles={stylesData.errors.window}/>,
    login: ComposedLogin,
    vote: <Vote />
  };

  const renderGrid = () => {
    return (
      <>
        { props.isCategoryVote && checkIfTrue(text.grid.settings.display_dropdown) &&
          <Dropdown /> }

        { props.isCategoryVote && checkIfTrue(settings.window_status) &&
          <User /> }

        <Grid>
          { checkIfTrue(text.ads.square.settings.display) &&
          <AdUnit size={constants.AD_UNITS.SQUARE} /> }

        </Grid>

        { props.isCategoryVote && checkIfTrue(text.grid.settings.display_navigation) &&
          <Navigation /> }
      </>
    )
  }

  return (
    <div className={css(styles.page)}>
        <div className={css(styles.app_container)} aria-hidden={props.modalProps.type !== ""}>

          {checkIfTrue(text.ads.leaderboard.settings.display) &&
            <AdUnit size={constants.AD_UNITS.LEADERBOARD} /> }

          {checkIfTrue(text.ads.mobile_leaderboard.settings.display) &&
            <AdUnit size={constants.AD_UNITS.MOBILE_LEADERBOARD} />}

          {checkIfTrue(text.header.settings.display) &&
            <Header>

              <Language onSelect={changeLanguage}
                        copy={copy.header.language}
                        languageStyles={stylesData.header.language}/>

              { checkIfTrue(text.ads.rectangle.settings.display) &&
                <AdUnit size={constants.AD_UNITS.RECTANGLE} /> }

              { checkIfTrue(text.ads.mobile_rectangle.settings.display) &&
                <AdUnit size={constants.AD_UNITS.MOBILE_RECTANGLE} /> }

            </Header> }

          { geo.inRegion?
            <main className={css(styles.app)} role='main'>

              { !checkIfTrue(settings.window_status) && !gridEnabled &&
                <Closed>
                  {checkIfTrue(text.closed.settings.display_countdown) &&
                    <Countdown key='countdown' />}
                </Closed> }

              { !props.isCategoryVote && checkIfTrue(settings.window_status) &&
                <User /> }

              <Switch>
                <Route exact={true} path='/' render={() => {
                  if (!gridEnabled) { return null; }

                  if ( props.isCategoryVote ) {
                    return (
                      <Categories>
                        { checkIfTrue(settings.window_status) &&
                        <User /> }
                      </Categories>
                    );
                  } else {
                    return renderGrid();
                  }
                }} />

                <Route path={["/:name/:detail", "/:name"]} render={() => {
                  if (!gridEnabled) { return null; }

                  return renderGrid();
                }} />
              </Switch>

              { checkIfTrue(text.ads.leaderboard_bottom.settings.display) &&
                <AdUnit size={constants.AD_UNITS.LEADERBOARD_BOTTOM} /> }

            { checkIfTrue(text.ads.mobile_leaderboard_bottom.settings.display) &&
              <AdUnit size={constants.AD_UNITS.MOBILE_LEADERBOARD_BOTTOM} /> }
          </main> :

          <ErrorMessage data={copy.errors.geo} styles={stylesData.errors.geo} track={constants.GA_PAGES.GEO}/> }

          { checkIfTrue(text.footer.settings.display) &&
            <Footer/> }
        </div>

        { modalMap[props.modalProps.type] && (
          <Modal>
            { modalMap[props.modalProps.type] }

            { checkIfTrue(text.ads.modal.settings.display) &&
            ( props.modalProps.type === constants.MODAL_TYPES.confirmation ||
              props.modalProps.type === constants.MODAL_TYPES.vote ) &&
            <AdUnit size={constants.AD_UNITS.MODAL} /> }
        </Modal>)}

        {checkIfTrue(props.cmsData.text.cookie_consent.enabled) && (
        <CookieConsent
          storageKey={`${wid}_${props.cmsData.text.cookie_consent.storage_key}`}
          onAccept={handleCookieConsentAccepted}
          onDecline={handleCookieConsentDeclined}
          acceptButtonLabel={copy.cookie_consent.accept_button_text}
          declineButtonLabel={copy.cookie_consent.decline_button_text}
          showDeclineButton={checkIfTrue(
            props.cmsData.text.cookie_consent.allow_decline_consent
          )}
          expires={parseInt(props.cmsData.text.cookie_consent.consent_expiry_in_days, 10)}
          layout={props.cmsData.text.cookie_consent.layout}
          styleOverrides={props.stylesData.cookieConsent}
        >
          <span
            dangerouslySetInnerHTML={{ __html: copy.cookie_consent.content }}
          />
        </CookieConsent>
      )}

      </div>
  )
}

export default withRouter(Connect(App));
